import React from 'react';
import { Dimensions } from '../../types';
import { formatDimensionValue } from '../../utils/conversions';

interface CaseOutlineProps {
  width: number;
  height: number;
  selected?: boolean;
  className?: string;
}

// Plain front-view rectangle, sized in px by the caller
export const CaseOutline: React.FC<CaseOutlineProps> = ({ width, height, selected = false, className = '' }) => (
  <span
    className={`
      relative block rounded-[3px] border-[1.5px] transition-colors duration-150
      ${selected ? 'border-accent bg-accent-soft' : 'border-fg bg-surface'}
      ${className}
    `}
    style={{ width, height }}
  >
    <span className="absolute left-[18%] right-[18%] top-[6%] h-0.5 bg-current opacity-40" />
  </span>
);

interface OrthoDrawingProps {
  dimensions: Dimensions;
  scale: number; // px per mm
  unit: 'mm' | 'in';
  selected?: boolean;
}

// Front + side orthographic views with dimension brackets (H on the left, W and D below)
export const OrthoDrawing: React.FC<OrthoDrawingProps> = ({ dimensions, scale, unit, selected = false }) => {
  const frontWidth = Math.round(dimensions.width * scale);
  const sideWidth = Math.round(dimensions.depth * scale);
  const height = Math.round(dimensions.height * scale);
  const viewStyles = selected ? 'border-accent bg-accent-soft text-accent' : 'border-fg bg-surface text-fg';

  return (
    <div className="flex items-end gap-3.5" aria-hidden="true">
      <div className="flex flex-col items-end">
        <div className="flex items-center gap-1" style={{ height }}>
          <span className="font-mono text-[10px] text-accent-ink">{formatDimensionValue(dimensions.height, unit)}</span>
          <span className="block w-[5px] h-full border border-r-0 border-accent" />
        </div>
        <div className="h-[22px]" />
      </div>

      <DrawingView width={frontWidth} height={height} label={formatDimensionValue(dimensions.width, unit)} className={viewStyles}>
        <span className="absolute left-[18%] right-[18%] top-[6%] h-0.5 bg-current opacity-50" />
        <span className="absolute left-[14%] right-[14%] top-[22%] bottom-[8%] rounded-sm border border-dashed border-current opacity-35" />
      </DrawingView>

      <DrawingView width={sideWidth} height={height} label={formatDimensionValue(dimensions.depth, unit)} className={viewStyles}>
        <span className="absolute inset-[7%] rounded-sm border border-dashed border-current opacity-35" />
      </DrawingView>
    </div>
  );
};

interface DrawingViewProps {
  width: number;
  height: number;
  label: string;
  className: string;
  children: React.ReactNode;
}

const DrawingView: React.FC<DrawingViewProps> = ({ width, height, label, className, children }) => (
  <div className="flex flex-col items-center gap-1">
    <div className={`relative rounded-[3px] border-[1.5px] transition-colors duration-150 ${className}`} style={{ width, height }}>
      {children}
    </div>
    <span className="block h-[5px] border border-t-0 border-accent" style={{ width }} />
    <span className="font-mono text-[10px] leading-[10px] text-accent-ink">{label}</span>
  </div>
);
